import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigation } from '../context/NavigationContext.js';
import languageLearningApi from "../components/language_learning/middleware/languageLearningApi.js";
import { 
  Autocomplete, 
  Button, 
  Card, 
  Chip, 
  Container, 
  FormControl, 
  Grid, 
  InputLabel, 
  MenuItem, 
  Paper, 
  Select, 
  TextField, 
  Typography,
  Box,
  Fade,
  Divider,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

// Theme colors
const THEME = {
  primary: '#4a8bf4',
  lightBlue: '#e8f2ff', // Very light sky blue
  white: '#ffffff',
  text: '#333333',
  border: '#d0e3ff'
};

const GameWrapper = ({ onGameEvent }) => {
  const { currentCourse } = useNavigation();
  const courseId = currentCourse?._id;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // References
  const iframeRef = useRef(null);
  const gameReadyRef = useRef(false);
  const vocabularyDataRef = useRef([]);
  const currentQuestionIndexRef = useRef(0);
  const sentQuestionsRef = useRef([]);
  const questionIntervalRef = useRef(null);
  
  // Game state
  const [isGameReady, setIsGameReady] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [showGame, setShowGame] = useState(false);
  const [showSettings, setShowSettings] = useState(true); // Initially show settings
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  
  // Form controls state
  const [sections, setSections] = useState([]);
  const [courselets, setCourselets] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedCourselets, setSelectedCourselets] = useState([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [customCount, setCustomCount] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  
  // Game settings
  const [gameSettings, setGameSettings] = useState({
    gameTime: 60, // Changed from 150 to 60 seconds
    lives: 5,      // 5 lives
    speed: 'medium'
  });
  
  // Fetch sections when component mounts and courseId is available
  useEffect(() => {
    if (courseId) {
      fetchSections();
    }
  }, [courseId]);
  
  // Fetch courselets when section is selected
  useEffect(() => {
    if (selectedSection) {
      fetchCourselets(selectedSection._id);
    } else {
      setCourselets([]);
    }
  }, [selectedSection]);
  
  // Function to send messages to the game
  const sendMessageToGame = useCallback((type, payload) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type, payload }, '*');
    }
  }, []);

  // Send next question to game
  const sendNextQuestion = useCallback(() => {
    // Safety checks
    if (!gameReadyRef.current || 
        vocabularyDataRef.current.length === 0 || 
        currentQuestionIndexRef.current >= vocabularyDataRef.current.length) {
      return;
    }
    
    // Find next non-duplicate question using an iterative approach
    let foundValidQuestion = false;
    let checkIndex = currentQuestionIndexRef.current;
    
    while (!foundValidQuestion && checkIndex < vocabularyDataRef.current.length) {
      const vocabItem = vocabularyDataRef.current[checkIndex];
      
      // Check if this question is already in the active questions
      const isAlreadySent = sentQuestionsRef.current.some(q => q.question === vocabItem.question);
      
      if (!isAlreadySent) {
        // We found a non-duplicate question
        foundValidQuestion = true;
        
        // Format as expected by game - send all possible answers
        const questionData = {
          question: vocabItem.question,
          answer: vocabItem.answers // Send all possible answers
        };
        
        console.log(`Sending question #${checkIndex + 1}: ${vocabItem.question}`);
        
        sendMessageToGame('NEW_QUESTION', questionData);
        
        // Add to sent questions
        sentQuestionsRef.current = [...sentQuestionsRef.current, vocabItem];
        
        // Update the current index to the next position
        currentQuestionIndexRef.current = checkIndex + 1;
      } else {
        // Skip duplicate and check the next question
        checkIndex++;
      }
    }
    
    // If we went through all questions and didn't find any new ones
    if (!foundValidQuestion) {
      console.log("No more new questions to send");
    }
  }, [sendMessageToGame]);
  
  // Start sending questions in intervals
  const startSendingQuestions = useCallback(() => {
    // Clear any existing interval
    if (questionIntervalRef.current) {
      clearInterval(questionIntervalRef.current);
    }
    
    setIsRunning(true);
    
    // Send initial question
    sendNextQuestion();
    
    // Set up interval to send new questions every 6 seconds
    const interval = setInterval(() => {
      if (isRunning && currentQuestionIndexRef.current < vocabularyDataRef.current.length) {
        // Only send new question if active questions is below threshold
        if (sentQuestionsRef.current.length < 3) { // Limit active questions to prevent overwhelming
          sendNextQuestion();
        }
      } else if (currentQuestionIndexRef.current >= vocabularyDataRef.current.length) {
        // All questions sent, stop interval
        clearInterval(interval);
      }
    }, 6000);
    
    questionIntervalRef.current = interval;
  }, [isRunning, sendNextQuestion]);
  
  // Stop sending questions
  const stopSendingQuestions = useCallback(() => {
    if (questionIntervalRef.current) {
      clearInterval(questionIntervalRef.current);
      questionIntervalRef.current = null;
    }
  }, []);
  
  // Handle messages from the game
  useEffect(() => {
    const handleGameMessage = (event) => {
      const { type, payload } = event.data;
      
      if (type === 'GAME_READY') {
        gameReadyRef.current = true;
        setIsGameReady(true);
        console.log("Game is ready to receive messages");
      } else if (type === 'GAME_STARTED') {
        // If game has started, start sending questions
        startSendingQuestions();
      } else if (type === 'GAME_ENDED') {
        stopSendingQuestions();
        setIsRunning(false);
      } else if (type === 'QUESTION_ANSWERED') {
        // Record that this question was answered
        if (payload && payload.question) {
          console.log(`Question answered: ${payload.question}`);
          
          // Find the index of the answered question in the sent questions array
          const indexInSent = sentQuestionsRef.current.findIndex(q => q.question === payload.question);
          
          if (indexInSent !== -1) {
            // Remove the question from the sent questions array
            const updatedSentQuestions = [...sentQuestionsRef.current];
            updatedSentQuestions.splice(indexInSent, 1);
            sentQuestionsRef.current = updatedSentQuestions;
            
            // If there are no active questions, send a new one immediately
            if (updatedSentQuestions.length === 0 && currentQuestionIndexRef.current < vocabularyDataRef.current.length) {
              setTimeout(() => sendNextQuestion(), 500); // Small delay for better gameplay
            }
          }
        }
      }
      
      if (onGameEvent) {
        onGameEvent(type, payload);
      }
    };

    window.addEventListener('message', handleGameMessage);
    return () => {
      window.removeEventListener('message', handleGameMessage);
      stopSendingQuestions();
    };
  }, [onGameEvent, startSendingQuestions, stopSendingQuestions, sendNextQuestion]);
  
  // Fetch sections from API
  const fetchSections = async () => {
    try {
      const response = await languageLearningApi.get(`/courses/${courseId}/sections`);
      setSections(response.data.sections);
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };
  
  // Fetch courselets from API
  const fetchCourselets = async (sectionId) => {
    try {
      const response = await languageLearningApi.get(`/courselets/${sectionId}/courselets`);
      setCourselets(response.data.courselets);
    } catch (error) {
      console.error("Error fetching courselets:", error);
    }
  };
  
  // Fetch vocabulary for selected courselets - only called once on game start
  const fetchVocabulary = async () => {
    try {
      const allVocab = [];
      
      for (const courselet of selectedCourselets) {
        const response = await languageLearningApi.get(`/vocabulary/${courselet._id}/listVocabulary`);
        console.log('Debug : listVocab response ', response);
        allVocab.push(...response.data.vocabulary);
      }
      
      // Shuffle vocabulary
      const shuffled = [...allVocab].sort(() => 0.5 - Math.random());
      
      // Limit by question count
      const limitedVocab = questionCount === "all" 
        ? shuffled 
        : shuffled.slice(0, parseInt(questionCount, 10));
      
      // Update the refs instead of state
      vocabularyDataRef.current = limitedVocab;
      currentQuestionIndexRef.current = 0;
      sentQuestionsRef.current = [];
      
      return limitedVocab;
    } catch (error) {
      console.error("Error fetching vocabulary:", error);
      return [];
    }
  };
  
  // Handle section selection
  const handleSectionSelect = (event, newValue) => {
    setSelectedSection(newValue);
    // Clear selected courselets when changing sections
    setSelectedCourselets([]);
  };
  
  // Handle courselet selection
  const handleCourseletSelect = (event, newValue) => {
    // Check if the "Select All" option was chosen
    if (newValue && newValue.id === 'select-all') {
      setSelectedCourselets([...courselets]);
    } else if (newValue && !selectedCourselets.find(c => c._id === newValue._id)) {
      setSelectedCourselets([...selectedCourselets, newValue]);
    }
  };
  
  // Handle removing a courselet
  const handleRemoveCourselet = (courseletId) => {
    setSelectedCourselets(selectedCourselets.filter(c => c._id !== courseletId));
  };
  
  // Handle question count selection
  const handleQuestionCountChange = (event) => {
    const value = event.target.value;
    if (value === "custom") {
      setShowCustomInput(true);
    } else {
      setShowCustomInput(false);
      setQuestionCount(value);
    }
  };
  
  // Handle custom question count input
  const handleCustomCountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setCustomCount(value);
      if (value !== "" && parseInt(value, 10) > 0) {
        setQuestionCount(parseInt(value, 10));
      }
    }
  };
  
  // Handle game setting changes - Memoized to prevent unnecessary re-renders
  const handleSettingChange = useCallback((setting, value) => {
    setGameSettings(prevSettings => ({
      ...prevSettings,
      [setting]: value
    }));
  }, []);
  
  // Toggle settings drawer
  const toggleSettingsDrawer = useCallback(() => {
    setSettingsDrawerOpen(prevState => !prevState);
  }, []);
  
  // Start game with selected courselets
  const startGame = useCallback(async () => {
    if (selectedCourselets.length === 0) {
      alert("Please select at least one courselet");
      return;
    }
    
    // Fetch vocabulary only once when starting the game
    const vocab = await fetchVocabulary();
    
    if (vocab.length === 0) {
      alert("No vocabulary found for selected courselets");
      return;
    }
    
    // Hide settings and show game
    setShowSettings(false);
    setShowGame(true);
    
    // Initialize game
    setIsRunning(true);
    
    // Send initial game configuration
    sendMessageToGame('INIT_GAME', gameSettings);
    
    // Start the game
    sendMessageToGame('START_GAME', {});
  }, [selectedCourselets, gameSettings, sendMessageToGame]);

  // Determine if we're in drawer mode - affects layout
  const isDrawerMode = useMemo(() => settingsDrawerOpen || (showGame && !showSettings), [settingsDrawerOpen, showGame, showSettings]);

  // Settings content component - Memoized to prevent unnecessary re-renders
  const SettingsContent = useCallback(() => {
    const controlsLayout = isDrawerMode || isSmallScreen ? 12 : 4;
    
    return (
      <Paper
        elevation={1}
        sx={{
          p: isDrawerMode ? 2 : 3,
          mb: 4,
          backgroundColor: THEME.white,
          borderRadius: 2,
          border: `1px solid ${THEME.border}`
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom sx={{ 
          color: THEME.primary, 
          fontWeight: 600,
          mb: 3,
          fontSize: isDrawerMode ? '1.2rem' : '1.5rem'
        }}>
          Game Setup
        </Typography>

        <Grid container spacing={isDrawerMode ? 2 : 3}>
          {/* Section Selection */}
          <Grid item xs={12} md={isDrawerMode ? 12 : 6}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, display: 'block' }}>
              Select Section
            </Typography>
            <Autocomplete
              id="section-select"
              options={sections}
              getOptionLabel={(option) => option.title || ""}
              value={selectedSection}
              onChange={handleSectionSelect}
              fullWidth
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              )}
            />
          </Grid>

          {/* Courselet Selection */}
          <Grid item xs={12} md={isDrawerMode ? 12 : 6}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, display: 'block' }}>
              Add Courselet
            </Typography>
            <Autocomplete
              id="courselet-select"
              options={[
                { id: 'select-all', title: '-- Select All Courselets --' },
                ...courselets
              ]}
              getOptionLabel={(option) => option.title || ""}
              onChange={handleCourseletSelect}
              disabled={!selectedSection}
              fullWidth
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  variant="outlined"
                  sx={{ mb: 2 }}
                  disabled={!selectedSection}
                />
              )}
            />
          </Grid>

          {/* Selected Courselets */}
          {selectedCourselets.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Selected Courselets
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 3,
                  p: 2,
                  backgroundColor: THEME.lightBlue,
                  borderRadius: 2,
                  minHeight: '60px'
                }}
              >
                {selectedCourselets.map((courselet) => (
                  <Chip
                    key={courselet._id}
                    label={courselet.title}
                    onDelete={() => handleRemoveCourselet(courselet._id)}
                    sx={{
                      backgroundColor: 'rgba(74, 139, 244, 0.15)',
                      color: THEME.primary,
                      fontSize: '0.95rem',
                      height: 'auto',
                      padding: '8px 4px',
                      '& .MuiChip-label': {
                        padding: '0 8px',
                        fontWeight: 500
                      },
                      '& .MuiChip-deleteIcon': {
                        color: THEME.primary,
                        '&:hover': {
                          color: '#3a7bd5'
                        }
                      },
                      borderColor: 'rgba(74, 139, 244, 0.3)',
                      border: '1px solid',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: isDrawerMode ? 1 : 2 }} />
          </Grid>

          {/* Game Settings */}
          <Grid item xs={12} md={isDrawerMode ? 12 : 6}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
              Number of Questions
            </Typography>
            <FormControl fullWidth variant="outlined">
              <Select
                value={showCustomInput ? "custom" : questionCount}
                onChange={handleQuestionCountChange}
                sx={{ mb: 2 }}
              >
                <MenuItem value="5">5 Questions</MenuItem>
                <MenuItem value="10">10 Questions</MenuItem>
                <MenuItem value="20">20 Questions</MenuItem>
                <MenuItem value="all">All Available</MenuItem>
                <MenuItem value="custom">Custom Number</MenuItem>
              </Select>
            </FormControl>
            
            {showCustomInput && (
              <TextField
                fullWidth
                variant="outlined"
                type="text"
                label="Custom Number of Questions"
                value={customCount}
                onChange={handleCustomCountChange}
                sx={{ mb: 2 }}
              />
            )}
          </Grid>

          <Grid item xs={12} md={isDrawerMode ? 12 : 6}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
              Game Settings
            </Typography>
            
            <Grid container spacing={isDrawerMode ? 2 : 2}>
              <Grid item xs={12} md={controlsLayout}>
                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <InputLabel>Speed</InputLabel>
                  <Select
                    value={gameSettings.speed}
                    onChange={(e) => handleSettingChange('speed', e.target.value)}
                    label="Speed"
                  >
                    <MenuItem value="slow">Slow</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="fast">Fast</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={controlsLayout}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Game Time (seconds)"
                  InputProps={{ 
                    inputProps: { min: 30, max: 300 } 
                  }}
                  value={gameSettings.gameTime}
                  onChange={(e) => handleSettingChange('gameTime', parseInt(e.target.value))}
                  sx={{ mb: 2 }}
                />
              </Grid>
              
              <Grid item xs={12} md={controlsLayout}>
                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <InputLabel>Lives</InputLabel>
                  <Select
                    value={gameSettings.lives}
                    onChange={(e) => handleSettingChange('lives', parseInt(e.target.value))}
                    label="Lives"
                  >
                    <MenuItem value={3}>3 Lives</MenuItem>
                    <MenuItem value={5}>5 Lives</MenuItem>
                    <MenuItem value={7}>7 Lives</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={startGame}
            disabled={selectedCourselets.length === 0}
            sx={{
              backgroundColor: THEME.primary,
              '&:hover': {
                backgroundColor: '#3a7bd5',
              },
              px: 4,
              borderRadius: 2
            }}
          >
            Start Game
          </Button>
        </Box>
      </Paper>
    );
  }, [
    isDrawerMode, 
    isSmallScreen, 
    sections, 
    selectedSection, 
    courselets, 
    selectedCourselets, 
    showCustomInput, 
    questionCount, 
    customCount, 
    gameSettings,
    handleSectionSelect, 
    handleCourseletSelect, 
    handleRemoveCourselet, 
    handleQuestionCountChange, 
    handleSettingChange, 
    startGame
  ]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Settings Display */}
      {showSettings && <SettingsContent />}
      
      {/* Settings Button (visible when game is shown) */}
      {showGame && !showSettings && (
        <Box sx={{ 
          position: 'relative', 
          display: 'flex', 
          justifyContent: 'flex-end',
          marginBottom: '8px'
        }}>
          <IconButton 
            onClick={toggleSettingsDrawer} 
            sx={{ 
              backgroundColor: THEME.primary, 
              color: THEME.white,
              '&:hover': {
                backgroundColor: '#3a7bd5',
              },
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
            }}
          >
            <SettingsIcon />
          </IconButton>
        </Box>
      )}
      
      {/* Settings Drawer */}
      <Drawer
        anchor="right"
        open={settingsDrawerOpen}
        onClose={toggleSettingsDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '450px' },
            padding: 2,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: THEME.primary, fontWeight: 600 }}>
              Game Settings
            </Typography>
            <IconButton onClick={toggleSettingsDrawer}>
              <SettingsIcon />
            </IconButton>
          </Box>
          <SettingsContent />
        </Box>
      </Drawer>
      
      {/* Game Area */}
      <Fade in={showGame} timeout={500}>
        <Box sx={{ display: showGame ? 'block' : 'none', minHeight: '600px' }}>
          <iframe
            ref={iframeRef}
            src="/test2.html"
            title="Game"
            style={{
              width: '100%',
              height: '600px',
              border: 'none',
              display: 'block',
              backgroundColor: THEME.lightBlue,
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              marginBottom: '16px'
            }}
            sandbox="allow-scripts allow-same-origin"
          />
        </Box>
      </Fade>
    </Container>
  );
};

export default GameWrapper;